'use client';

import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
//
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { getIsAddonWasPurshasedEarly } from '@nx-frontend/shared/api/webnotes/get-is-addon-was-purchased-early';
import { generalEnv } from '@nx-frontend/shared/env';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $hasCoupons, $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { TranscriptStatus, $transcriptPanel } from '@nx-frontend/shared/store/transcript-panel';
import { $user } from '@nx-frontend/shared/store/user';
import { BillingAddonCode, BillingInfoPlan, PaywallPopupReason, PopupType } from '@nx-frontend/shared/types';
import { hasMaxSubscriptionPlan } from '@nx-frontend/shared/utils';
import { getOpenAddonPopup, getOpenPaywallPopup } from '@nx-frontend/widgets/popups';

export const useTranscriptLimit = (): boolean => {
  const [
    app,
    transcriptPanel,
    openModal,
    orgInfo,
    currentOrganization,
    user,
    hasCoupons,
    billingInfo,
    currentWorkspace,
    orgCoupons,
  ] = useUnit([
    $app,
    $transcriptPanel,
    openModalEvent,
    $orgInfo,
    $currentOrganization,
    $user,
    $hasCoupons,
    $billingInfo,
    $currentWorkspace,
    $orgCoupons,
  ]);

  const t = useTranslations('features');

  const attachmentGlobalId = transcriptPanel.attachment.globalId;

  const isLimitError = useMemo(() => {
    return (
      transcriptPanel.opened && transcriptPanel.entities[attachmentGlobalId]?.status === TranscriptStatus.LIMIT_ERROR
    );
  }, [attachmentGlobalId, transcriptPanel]);

  useEffect(() => {
    const onLimitErrorShowPopup = async () => {
      if (currentOrganization.userId === user.id) {
        // owner
        if (billingInfo.plan && billingInfo.plan !== BillingInfoPlan.TRIAL) {
          const isMaxSubscriptionPlan = hasMaxSubscriptionPlan(orgInfo, billingInfo, orgCoupons);
          const isAddonWasPurshasedEarlyOrMaxPlan = !isMaxSubscriptionPlan
            ? await getIsAddonWasPurshasedEarly({
                gatewayUrl: generalEnv.gatewayUrl,
                subscriptionId: billingInfo.subscriptionId,
                addon: BillingAddonCode.TRANSCRIBE,
                sessionId: app.sessionId,
              })
            : true;
          if (!isAddonWasPurshasedEarlyOrMaxPlan) {
            getOpenPaywallPopup({
              openModal,
              payload: {
                payloadType: PopupType.PAYWALL,
                mode: 'upgrade',
                title: t('paywall.title_unlock_the_magic_of_transcription'),
                reason: PaywallPopupReason.TRANSCRIBE,
                orgId: orgInfo.orgId,
              },
            });
          } else {
            getOpenAddonPopup({
              openModal,
              payload: {
                payloadType: PopupType.ADDON,
                code: BillingAddonCode.TRANSCRIBE,
              },
            });
          }
        } else {
          if (hasCoupons) {
            openModal({
              content: PopupType.TRANSCRIPT_LIMIT_LTD,
              showCloseButton: false,
            });
          } else {
            getOpenPaywallPopup({
              openModal,
              payload: {
                payloadType: PopupType.PAYWALL,
                mode: 'upgrade',
                title: t('paywall.title_unlock_the_magic_of_transcription'),
                reason: PaywallPopupReason.TRANSCRIBE,
                orgId: orgInfo.orgId,
              },
            });
          }
        }
      } else {
        // other organization users
        openModal({
          title: t('transcript.transcription_requests_limit_reached'),
          content: PopupType.DIALOG,
          closeButtonSize: 18,
          payload: {
            payloadType: PopupType.DIALOG,
            text: t('transcript.transcription_available_in_addon'),
            buttons: [
              {
                text: t('transcript.ok'),
                main: true,
              },
            ],
          },
        });
      }
    };
    if (isLimitError) {
      onLimitErrorShowPopup();
    }
  }, [
    isLimitError,
    openModal,
    orgInfo,
    user.id,
    currentOrganization.userId,
    hasCoupons,
    currentWorkspace,
    billingInfo.plan,
    attachmentGlobalId,
    billingInfo.subscriptionId,
    currentOrganization.globalId,
    app.sessionId,
    billingInfo,
    orgCoupons,
    t,
  ]);

  return isLimitError;
};
